import {state, transition, trigger, style, animate} from "@angular/animations";
import {Component, Input, OnDestroy, OnInit} from "@angular/core";
import {ActivatedRoute, Params, Router} from "@angular/router";
import {TranslocoService} from "@ngneat/transloco";
import {AbstractComponent} from "@components";
import {Message, TOAST_STATE} from "@model";
import {LoaderService, MessageService, ToastService} from "@services";
import {ORGANISER_MESSAGES, USER_MESSAGES} from "../../../../app-routing.module";

@Component({
    selector: 'app-messages.component',
    templateUrl: 'messages.component.html',
    styleUrls: ['messages.component.scss'],
    animations: [
        trigger('fadeInAnimation', [
            state('in', style({ opacity: 1 })),
            transition(':enter', [
                style({ opacity: 0 }),
                animate(200)
            ])
        ])
    ],
    standalone: false
})
export class MessagesComponent extends AbstractComponent implements OnInit, OnDestroy {

  @Input() messages: Message[] | undefined;
  @Input() isAdmin: boolean = false;

  activeNav: MessageTab = MessageTab.UNREAD;
  isLoading: boolean = false;
  queryParamsFirstChange: boolean = true;

  constructor(private messageService: MessageService, private translocoService: TranslocoService,
              private route: ActivatedRoute, private router: Router, private toastService: ToastService,
              private loaderService:LoaderService) {
    super();
  }

  ngOnInit(): void {
    this.subscribeToQueryParamsChange();
  }

  subscribeToQueryParamsChange(): void {
    this.subscriptions.push(this.route.queryParams.subscribe((params: Params) => {
        const activeNav: MessageTab = params['read'] === 'true' ? MessageTab.READ : MessageTab.UNREAD;
        if (!this.queryParamsFirstChange) {

          this.messageService.getUserMessages(activeNav === MessageTab.READ, () => {
            this.isLoading = true;
          }, () => {
            setTimeout(() => {
              this.isLoading = false;
            }, 300);
          }).subscribe((messages: Message[]) => {
            this.messages = messages;
            this.activateTab(activeNav);
          });
        } else {
          this.queryParamsFirstChange = false;
          this.activateTab(activeNav);
        }
      })
    );
  }

  private activateTab(activeNav: MessageTab) {
    this.activeNav = activeNav;
  }


  onMarkAsRead(messageId: number | undefined) {
    if (messageId) {
      this.loaderService.startLoading();
      this.messageService.getMessageById(messageId, () => {
        this.loaderService.dismissLoading();
        this.toastService.showToast(TOAST_STATE.success,
          this.translocoService.translate("app.common.message.marked.as.read.success"));
        this.messageService.getUserMessages(this.activeNav === MessageTab.READ).subscribe((messages: Message[]) => {
          this.messages = messages;
        });
      }, () => {
        this.loaderService.dismissLoading();
        this.toastService.showToast(TOAST_STATE.danger,
          this.translocoService.translate("app.common.message.marked.as.read.error"));
      });
    }
  }

  onDeleteAll() {
    if ((this.messages?.length ?? 0) > 0) {
      this.loaderService.startLoading();
      this.messageService.deleteMessages(this.messages!.map((item:Message)=>item.id!), () => {
        this.loaderService.dismissLoading();
        this.toastService.showToast(TOAST_STATE.success,
          this.translocoService.translate("app.common.messages.deleted.success"));
        this.messageService.getUserMessages(this.activeNav === MessageTab.READ).subscribe((messages: Message[]) => {
          this.messages = messages;
        });
      }, () => {
        this.loaderService.dismissLoading();
        this.toastService.showToast(TOAST_STATE.danger,
          this.translocoService.translate("app.common.messages.deleted.error"));
      });
    }
  }

  onAddMessage(message: Message | undefined) {
    if (message) {
      this.messageService.addMessage(message, () => {
        this.toastService.showToast(TOAST_STATE.success,
          this.translocoService.translate("Message successfully added."));
      }, () => {
        this.toastService.showToast(TOAST_STATE.danger,
          this.translocoService.translate("Error! Message has not been added."));
      });
    }
  }

  onDeleteMessage(messageId: number | undefined): void {
    if (messageId) {
      this.loaderService.startLoading();
      this.messageService.deleteMessages([messageId], () => {
        this.loaderService.dismissLoading();
        this.toastService.showToast(TOAST_STATE.success,
          this.translocoService.translate("app.common.message.deleted.success"));
        this.messageService.getUserMessages(this.activeNav === MessageTab.READ).subscribe((messages: Message[]) => {
          this.messages = messages;
        });
      }, () => {
        this.loaderService.dismissLoading();
        this.toastService.showToast(TOAST_STATE.danger,
          this.translocoService.translate("app.common.message.deleted.error"));
      });
    }
  }

  public navigate(id: number | undefined) {
    const filter: boolean = id === MessageTab.READ;
    this.router.navigate([this.isAdmin ? ORGANISER_MESSAGES : USER_MESSAGES], {queryParams: {read: filter}});
  }

  protected readonly MessageTab = MessageTab;
}

enum MessageTab {
  UNREAD = 1,
  READ = 2,
}
